/**
 * Send an event only if it can go out on the live connection right now.
 *
 * Socket.IO buffers anything emitted while it is disconnected (or while a
 * heartbeat has silently expired, e.g. a phone waking from sleep) and flushes the
 * buffer on reconnect. For a cashout that is dangerous: a tap made during a
 * tunnel would land seconds later at a multiplier the player never chose. The
 * volatile flag is not a fix either, because it also drops packets whenever the
 * transport is momentarily busy with another write.
 *
 * Returns false, and leaves nothing queued, when the event was not sent.
 */
interface EmittingSocket {
    connected: boolean;
    emit: (event: string, ...args: any[]) => unknown;
    sendBuffer?: Array<{ data?: unknown[] }>;
}

export function emitIfConnected(socket: EmittingSocket | null | undefined, event: string, payload: unknown): boolean {
    if (!socket || !socket.connected) return false;
    const buffered = socket.sendBuffer?.length ?? 0;
    socket.emit(event, payload);
    const buffer = socket.sendBuffer;
    if (buffer && buffer.length > buffered) {
        // The client thought it was connected but queued the packet anyway (an
        // expired heartbeat). Take it back out so a reconnect cannot deliver it.
        socket.sendBuffer = buffer.filter((packet, index) => index < buffered || packet?.data?.[0] !== event);
        return false;
    }
    return true;
}
